import { Token } from '@/domain/types/Token'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { zeroAddress, formatUnits, Address } from 'viem'
import { useChainId, useReadContracts } from 'wagmi'
import { useAccount } from 'wagmi'
import { BORING_VAULT_ABI } from '@/config/abis/vaults/boringVaultAbi'
import {
  VAULT_ADDRESSES,
  VaultHyperbeatMetadata,
  VaultNucleustMetadata,
  VAULTS_METADATA,
  VaultType,
} from '../types/vaults'
import { QUERY_REFETCH_INTERVAL } from '@/config/consts'
import { useMarketInfo } from '@/domain/market-info/useMarketInfo'

export interface VaultData {
  tvl: NormalizedUnitNumber
  currentlySupplied: NormalizedUnitNumber | undefined
  userVaultBalance: NormalizedUnitNumber | undefined
  userWalletBalances: Record<string, NormalizedUnitNumber> | undefined
  apy: NormalizedUnitNumber
  exchangeRate?: string
  metadata: VaultNucleustMetadata | VaultHyperbeatMetadata
}

export interface UseVaultsResults {
  isLoading: boolean
  vaults: VaultData[]
}

export function useVaults(): UseVaultsResults {
  const { address } = useAccount()
  const chainId = useChainId()

  const { marketInfo } = useMarketInfo({ chainId, refetchInterval: QUERY_REFETCH_INTERVAL })

  const { data: totalSupplies, isLoading: isLoadingTVL } = useReadContracts({
    contracts: VAULT_ADDRESSES.map((vaultAddress) => ({
      address: vaultAddress as Address,
      abi: BORING_VAULT_ABI,
      functionName: 'totalSupply',
    })),
  })

  const { data: userVaultBalances, isLoading: isLoadingVaultBalances } = useReadContracts({
    contracts: VAULT_ADDRESSES.map((vaultAddress) => ({
      address: vaultAddress as Address,
      abi: BORING_VAULT_ABI,
      functionName: 'balanceOf',
      args: [address || zeroAddress],
    })),
    query: {
      enabled: !!address,
    },
  })

  // Combine all data into vault objects
  const vaults = VAULT_ADDRESSES.map((vaultAddress, index) => {
    const metadata = VAULTS_METADATA[vaultAddress]

    if (!metadata) {
      return null
    }

    const totalSupply = totalSupplies?.[index]?.result || BigInt(0)
    const userVaultBalance = userVaultBalances?.[index]?.result || BigInt(0)
    const apy = metadata.type === VaultType.HYPERBEAT ? metadata.apy.total.toNumber() / 100 : 0

    const baseToken = marketInfo.reserves.find((r) => r.token.address === metadata.contracts.baseTokenAddress)

    const newMetadata: VaultNucleustMetadata | VaultHyperbeatMetadata = {
      ...metadata,
      supportTokens: metadata.supportTokens.map(
        (token) => new Token({ ...token, unitPriceUsd: baseToken?.priceInUSD.toString() || '1' }),
      ),
      vaultToken: new Token({ ...metadata.vaultToken, unitPriceUsd: baseToken?.priceInUSD.toString() || '1' }),
    }

    return {
      metadata: newMetadata,
      tvl: NormalizedUnitNumber(formatUnits(totalSupply as bigint, newMetadata.vaultToken.decimals)),
      userVaultBalance: userVaultBalance
        ? NormalizedUnitNumber(formatUnits(userVaultBalance as bigint, newMetadata.vaultToken.decimals))
        : undefined,
      userWalletBalances: undefined,
      apy: NormalizedUnitNumber(apy),
      currentlySupplied: undefined,
    }
  })

  return {
    isLoading: isLoadingTVL || isLoadingVaultBalances,
    vaults: vaults.filter((v) => v !== null),
  }
}
